import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Table primitives (T-202 Slice 3) — the single owner of listing-table chrome.
 *
 * Bakes in the mandated §7 header string, one standard row hover, and consistent
 * `px-4 py-3` cell padding so every listing renders identically. Composable
 * (`<Table><THead><Tr><Th/></Tr></THead><TBody>…</TBody></Table>`) rather than a
 * config object, so callers keep full control of cells.
 *
 * - Header uses the RTL-safe logical `text-start` (the css-guard forbids the physical
 *   left/right text alignment utilities; ui-system §7 updated to match).
 * - `<Table>` wraps the table in `overflow-x-auto` so wide tables scroll instead of
 *   overflowing; pair with the `.table-mobile-cards` CSS pattern for card layouts
 *   under `sm` (the repo's existing mobile convention).
 */

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  /** Render each row as a card under 767px (the `.table-mobile-cards` pattern).
   *  Pair with `<Td label="…">` so each cell shows its column label on mobile. */
  mobileCards?: boolean;
}

export function Table({ className, mobileCards = false, ...props }: TableProps) {
  return (
    <div className={cn('w-full overflow-x-auto', mobileCards && 'table-mobile-cards')}>
      <table className={cn('w-full text-sm', className)} {...props} />
    </div>
  );
}

export function THead({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn('border-b border-border', className)} {...props} />;
}

export function TBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={className} {...props} />;
}

export interface TrProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Omit the standard hover (e.g. header rows, totals rows). */
  hover?: boolean;
}

export function Tr({ className, hover = true, ...props }: TrProps) {
  return (
    <tr
      className={cn(
        'border-b border-border last:border-0',
        hover && 'transition-colors hover:bg-muted/50',
        className,
      )}
      {...props}
    />
  );
}

export function Th({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        'px-4 py-3 text-start text-xs font-semibold uppercase tracking-wide text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

export interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /** Column label shown before the value on mobile cards (sets `data-label`). */
  label?: string;
}

export function Td({ className, label, ...props }: TdProps) {
  return <td className={cn('px-4 py-3 align-middle', className)} data-label={label} {...props} />;
}
